import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './api'
import { cue } from './sound'

/** Every admin call, in one place. Types come from the server's route defs. */

export async function fetchSession() {
  return unwrap(await api.auth.session.$get())
}

export function useLogin() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (password: string) => unwrap(await api.auth.login.$post({ json: { password } })),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['session'] }),
  })
}

export function useLogout() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => unwrap(await api.auth.logout.$post()),
    onSuccess: () => queryClient.clear(),
  })
}

export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: async () => unwrap(await api.admin.stats.$get()),
  })
}

export function useLibrary() {
  return useQuery({
    queryKey: ['admin', 'library'],
    queryFn: async () => unwrap(await api.admin.games.$get()),
  })
}

export function useAdminStores() {
  return useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: async () => unwrap(await api.admin.stores.$get()),
    staleTime: 5 * 60_000,
  })
}

export function useAdminSources() {
  return useQuery({
    queryKey: ['admin', 'sources'],
    queryFn: async () => unwrap(await api.admin.sources.$get()),
    staleTime: 5 * 60_000,
  })
}

/**
 * Quick-add search. Disabled below two characters so a single keystroke never
 * fires a provider request.
 */
export function useGameSearch(query: string) {
  const term = query.trim()

  return useQuery({
    queryKey: ['admin', 'search', term],
    queryFn: async () => unwrap(await api.admin.search.$get({ query: { q: term } })),
    enabled: term.length >= 2,
    staleTime: 5 * 60_000,
    // Keeps the previous list on screen while the next one loads, so the
    // dropdown does not flicker empty on every keystroke.
    placeholderData: (previous) => previous,
  })
}

export function useImportGame() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { provider: 'steam' | 'igdb'; externalId: string }) =>
      unwrap(await api.admin.games.import.$post({ json: input })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'library'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export type AddKeysInput = {
  gameId: string
  storeId: string
  sourceId?: string
  newSourceName?: string
  codes: string
  unlockType?: 'base' | 'bundle' | 'dlc' | 'other'
  edition?: string
  expiresAt?: string
  note?: string
}

export function useAddKeys() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: AddKeysInput) => unwrap(await api.admin.keys.$post({ json: input })),
    onSuccess: () => {
      cue('sent')
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
    onError: () => cue('failed'),
  })
}

export function useGameKeys(gameId: string | null) {
  return useQuery({
    queryKey: ['admin', 'keys', gameId],
    queryFn: async () => unwrap(await api.admin.games[':id'].keys.$get({ param: { id: gameId! } })),
    enabled: Boolean(gameId),
  })
}

/** Decrypts one key. Deliberately a mutation — it is an action, not a read. */
export function useRevealKey() {
  return useMutation({
    mutationFn: async (keyId: string) =>
      unwrap(await api.admin.keys[':id'].reveal.$post({ param: { id: keyId } })),
  })
}

export function useUpdateKeyStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { keyId: string; status: 'available' | 'claimed' | 'revoked' | 'expired' | 'reserved' }) =>
      unwrap(
        await api.admin.keys[':id'].$patch({
          param: { id: input.keyId },
          json: { status: input.status },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export function useDeleteKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (keyId: string) =>
      unwrap(await api.admin.keys[':id'].$delete({ param: { id: keyId } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

/* --- requests ------------------------------------------------------------ */

export function useRequests(status?: 'pending' | 'approved' | 'declined' | 'cancelled') {
  return useQuery({
    queryKey: ['admin', 'requests', status ?? 'all'],
    queryFn: async () =>
      unwrap(await api.admin.requests.$get({ query: status ? { status } : {} })),
    // The queue is the one thing worth seeing promptly without a reload.
    refetchInterval: 60_000,
  })
}

/** Keys that could be handed over for a request, for the approval picker. */
export function useAssignableKeys(requestId: string | null) {
  return useQuery({
    queryKey: ['admin', 'requests', requestId, 'keys'],
    queryFn: async () =>
      unwrap(await api.admin.requests[':id'].keys.$get({ param: { id: requestId! } })),
    enabled: Boolean(requestId),
  })
}

export function useApproveRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { requestId: string; keyId: string; adminNote?: string }) =>
      unwrap(
        await api.admin.requests[':id'].approve.$post({
          param: { id: input.requestId },
          json: { keyId: input.keyId, ...(input.adminNote ? { adminNote: input.adminNote } : {}) },
        }),
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export function useDeclineRequest() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (input: { requestId: string; adminNote?: string }) =>
      unwrap(
        await api.admin.requests[':id'].decline.$post({
          param: { id: input.requestId },
          json: input.adminNote ? { adminNote: input.adminNote } : {},
        }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })
}

/**
 * Removes a game entirely. The server refuses while it still has keys, so a
 * mis-click cannot take real codes with it.
 */
export function useDeleteGame() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (gameId: string) =>
      unwrap(await api.admin.games[':id'].$delete({ param: { id: gameId } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

/* --- recycle bin --------------------------------------------------------- */

export function useDeleted() {
  return useQuery({
    queryKey: ['admin', 'deleted'],
    queryFn: async () => unwrap(await api.admin.deleted.$get()),
  })
}

export function useRestoreGame() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (gameId: string) =>
      unwrap(await api.admin.games[':id'].restore.$post({ param: { id: gameId } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

export function useRestoreKey() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (keyId: string) =>
      unwrap(await api.admin.keys[':id'].restore.$post({ param: { id: keyId } })),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
    },
  })
}

/* --- editing a game ------------------------------------------------------ */

export function useGameDetail(gameId: string) {
  return useQuery({
    queryKey: ['admin', 'game', gameId],
    queryFn: async () => unwrap(await api.admin.games[':id'].$get({ param: { id: gameId } })),
  })
}

export type UpdateGameInput = {
  title?: string
  type?: 'game' | 'dlc' | 'bundle' | 'software'
  summary?: string | null
  releaseDate?: string | null
  developer?: string | null
  publisher?: string | null
  steamAppId?: number | null
  igdbId?: number | null
  isHidden?: boolean
  genreNames?: string[]
  platformSlugs?: string[]
}

/** Invalidates everything a game appears in: library, catalog, its own page. */
function useGameMutation<TArgs, TResult>(
  gameId: string,
  // Generic in its result too, so the typed RPC response survives the wrapper
  // and callers can read what the server actually returned.
  run: (args: TArgs) => Promise<TResult>,
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      cue('saved')
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
      queryClient.invalidateQueries({ queryKey: ['game'] })
      queryClient.invalidateQueries({ queryKey: ['admin', 'game', gameId] })
    },
    onError: () => cue('failed'),
  })
}

export function useUpdateGame(gameId: string) {
  return useGameMutation(gameId, async (input: UpdateGameInput) =>
    unwrap(await api.admin.games[':id'].$patch({ param: { id: gameId }, json: input })),
  )
}

export function useResyncGame(gameId: string) {
  return useGameMutation(gameId, async (provider: 'steam' | 'igdb') =>
    unwrap(await api.admin.games[':id'].resync.$post({ param: { id: gameId }, json: { provider } })),
  )
}

export function useSetStoreLinks(gameId: string) {
  return useGameMutation(gameId, async (links: { storeSlug: string; url: string }[]) =>
    unwrap(
      await api.admin.games[':id']['store-links'].$put({ param: { id: gameId }, json: { links } }),
    ),
  )
}

export function useSetArtworkUrl(gameId: string) {
  return useGameMutation(gameId, async (input: { slot: 'capsule' | 'hero' | 'logo'; url: string }) =>
    unwrap(
      await api.admin.games[':id'].artwork[':slot'].$post({
        param: { id: gameId, slot: input.slot },
        json: { url: input.url },
      }),
    ),
  )
}

export function useClearArtwork(gameId: string) {
  return useGameMutation(gameId, async (slot: 'capsule' | 'hero' | 'logo') =>
    unwrap(
      await api.admin.games[':id'].artwork[':slot'].$delete({
        param: { id: gameId, slot },
      }),
    ),
  )
}

/**
 * Uploads artwork. Uses fetch directly rather than the typed client: this is a
 * multipart body, not JSON, and the client models JSON routes.
 */
export function useUploadArtwork(gameId: string) {
  return useGameMutation(gameId, async (input: { slot: 'capsule' | 'hero' | 'logo'; file: File }) => {
    const form = new FormData()
    form.append('file', input.file)

    const response = await fetch(`/api/admin/games/${gameId}/artwork/${input.slot}/upload`, {
      method: 'POST',
      body: form,
    })

    if (!response.ok) {
      const body = (await response.json().catch(() => ({}))) as { error?: string }
      throw new Error(body.error ?? `Upload failed (${response.status})`)
    }

    return response.json()
  })
}

export function useCacheGameArtwork(gameId: string) {
  return useGameMutation(gameId, async () =>
    unwrap(await api.admin.games[':id'].images.cache.$post({ param: { id: gameId } })),
  )
}

/* --- editing keys, stores and sources ------------------------------------ */

/** Invalidates the admin tables and the public counts that derive from them. */
function useAdminMutation<TArgs, TResult>(run: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: run,
    onSuccess: () => {
      cue('saved')
      queryClient.invalidateQueries({ queryKey: ['admin'] })
      queryClient.invalidateQueries({ queryKey: ['catalog'] })
      queryClient.invalidateQueries({ queryKey: ['game'] })
    },
    onError: () => cue('failed'),
  })
}

export type UpdateKeyInput = {
  storeId?: string
  sourceId?: string | null
  unlockType?: 'base' | 'bundle' | 'dlc' | 'other'
  edition?: string | null
  expiresAt?: string | null
  note?: string | null
  code?: string
}

export function useUpdateKey(keyId: string) {
  return useAdminMutation(async (input: UpdateKeyInput) =>
    unwrap(await api.admin.keys[':id'].$patch({ param: { id: keyId }, json: input })),
  )
}

export function useCreateStore() {
  return useAdminMutation(async (input: { name: string; brandColor?: string; urlTemplate?: string | null }) =>
    unwrap(await api.admin.stores.$post({ json: input })),
  )
}

export function useUpdateStore(storeId: string) {
  return useAdminMutation(
    async (input: {
      name?: string
      brandColor?: string
      urlTemplate?: string | null
      isActive?: boolean
      sortOrder?: number
    }) => unwrap(await api.admin.stores[':id'].$patch({ param: { id: storeId }, json: input })),
  )
}

type SourceInput = {
  name: string
  brandColor?: string
  url?: string | null
  note?: string | null
  sortOrder?: number
}

export function useCreateSource() {
  return useAdminMutation(async (input: SourceInput) =>
    unwrap(await api.admin.sources.$post({ json: input })),
  )
}

export function useUpdateSource(sourceId: string) {
  return useAdminMutation(async (input: Partial<SourceInput>) =>
    unwrap(await api.admin.sources[':id'].$patch({ param: { id: sourceId }, json: input })),
  )
}
